/**
 * Community Movement Section
 * Features: Counting animation, tab switching, reviews slider
 * Sereyna branding with clean, accessible interactions
 */

class CommunityMovement {
  constructor() {
    this.sections = document.querySelectorAll('.community-movement');
    this.animatedStats = new Set();
    
    if (this.sections.length > 0) {
      this.init();
    }
  }

  init() {
    this.sections.forEach(section => {
      this.initTabs(section);
      this.initSlider(section);
      this.initCounters(section);
    });
  }

  /**
   * Initialize tab functionality
   */
  initTabs(section) {
    const tabs = section.querySelectorAll('.community-movement__tab');
    
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchTab(section, tab);
      });
    });
    
    // Set default active tab
    const defaultTab = section.querySelector('.community-movement__tab--active');
    if (defaultTab) {
      this.switchTab(section, defaultTab, false);
    }
  }

  /**
   * Switch active tab and dispatch custom event
   */
  switchTab(section, activeTab, animate = true) {
    const tabs = section.querySelectorAll('.community-movement__tab');
    const tabLabel = activeTab.dataset.tabLabel;
    
    // Remove active class from all tabs
    tabs.forEach(tab => {
      tab.classList.remove('community-movement__tab--active');
    });
    
    // Add active class to clicked tab
    activeTab.classList.add('community-movement__tab--active');
    
    // Dispatch custom event for external filtering
    window.dispatchEvent(new CustomEvent('communityTabChange', {
      detail: { tabLabel: tabLabel }
    }));
    
    // Re-animate counters if needed
    if (animate) {
      this.resetCounters(section);
      setTimeout(() => {
        this.animateCounters(section);
      }, 100);
    }
  }

  /**
   * Equalize heights of review cards
   */
  equalizeReviewHeights(reviews) {
    if (!reviews || reviews.length === 0) return;
    
    // Wait a bit for layout to settle
    setTimeout(() => {
      // Reset heights to auto to get natural height
      reviews.forEach(review => {
        review.style.height = 'auto';
        review.style.minHeight = 'auto';
      });
      
      // Force a reflow
      void reviews[0].offsetHeight;
      
      // Find the tallest card
      let maxHeight = 0;
      reviews.forEach(review => {
        const height = review.offsetHeight;
        if (height > maxHeight) {
          maxHeight = height;
        }
      });
      
      // Set all cards to the same height
      if (maxHeight > 0) {
        reviews.forEach(review => {
          review.style.height = maxHeight + 'px';
          review.style.minHeight = maxHeight + 'px';
        });
      }
    }, 50);
  }

  /**
   * Initialize reviews slider
   */
  initSlider(section) {
    const slider = section.querySelector('.community-movement__slider');
    if (!slider) return;
    
    const track = slider.querySelector('.community-movement__slider-track');
    const reviews = track.querySelectorAll('.community-movement__review');
    const prevBtn = slider.querySelector('.community-movement__slider-arrow--prev');
    const nextBtn = slider.querySelector('.community-movement__slider-arrow--next');
    const dotsContainer = slider.querySelector('.community-movement__slider-dots');
    
    if (reviews.length === 0) return;
    
    // Equalize review card heights
    // Removed equalizeReviewHeights - cards now use natural height based on content
    
    // Get responsive settings
    const slidesDesktop = parseInt(slider.dataset.slidesDesktop) || 3;
    const slidesTablet = parseInt(slider.dataset.slidesTablet) || 2;
    const slidesMobile = parseInt(slider.dataset.slidesMobile) || 1;
    const autoplay = slider.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(slider.dataset.autoplaySpeed) || 5000;
    const loop = slider.dataset.loop === 'true';
    
    let currentSlide = 0;
    let slidesPerView = this.getSlidesPerView(slidesDesktop, slidesTablet, slidesMobile);
    let autoplayInterval = null;
    
    // Update slider layout (defined first so it can be used by other functions)
    const updateSlider = () => {
      slidesPerView = this.getSlidesPerView(slidesDesktop, slidesTablet, slidesMobile);
      
      // On mobile, always show 1 card per slide for better UX
      const isMobile = window.innerWidth <= 768;
      const effectiveSlidesPerView = isMobile ? 1 : slidesPerView;
      
      // Calculate slide width based on effective slides per view
      const slideWidth = 100 / effectiveSlidesPerView;
      
      reviews.forEach((review, index) => {
        if (isMobile) {
          // On mobile, use full width minus gap
          const containerWidth = slider.offsetWidth || track.parentElement.offsetWidth;
          const gap = 12; // Match CSS gap
          const cardWidth = containerWidth - gap;
          review.style.flex = `0 0 ${cardWidth}px`;
          review.style.width = `${cardWidth}px`;
          review.style.minWidth = `${cardWidth}px`;
          review.style.maxWidth = `${cardWidth}px`;
        } else {
          // On desktop/tablet, use percentage
          review.style.flex = `0 0 ${slideWidth}%`;
          review.style.width = '';
          review.style.minWidth = '';
          review.style.maxWidth = '';
        }
      });
      
      const maxSlide = Math.max(0, reviews.length - effectiveSlidesPerView);
      currentSlide = Math.min(currentSlide, maxSlide);
      
      // Calculate translateX based on card width on mobile
      if (isMobile && reviews.length > 0) {
        const containerWidth = slider.offsetWidth || track.parentElement.offsetWidth;
        const gap = 12;
        const cardWidth = containerWidth - gap;
        const translateX = -(currentSlide * (cardWidth + gap));
        track.style.transform = `translateX(${translateX}px)`;
      } else {
        this.updateSliderPosition(track, currentSlide, slideWidth);
      }
      
      this.updateDots(dotsContainer, currentSlide, effectiveSlidesPerView);
      this.updateArrows(prevBtn, nextBtn, currentSlide, maxSlide, loop);
    };
    
    // Navigation functions
    const goToSlide = (index) => {
      const isMobile = window.innerWidth <= 768;
      const effectiveSlidesPerView = isMobile ? 1 : slidesPerView;
      const maxSlide = Math.max(0, reviews.length - effectiveSlidesPerView);
      currentSlide = Math.max(0, Math.min(index, maxSlide));
      updateSlider();
    };
    
    // Create dots if enabled
    const isMobileInitial = window.innerWidth <= 768;
    const effectiveSlidesPerViewInitial = isMobileInitial ? 1 : slidesPerView;
    if (dotsContainer && reviews.length > effectiveSlidesPerViewInitial) {
      this.createDots(dotsContainer, reviews.length, effectiveSlidesPerViewInitial, goToSlide);
    }
    
    const nextSlide = () => {
      const isMobile = window.innerWidth <= 768;
      const effectiveSlidesPerView = isMobile ? 1 : slidesPerView;
      const maxSlide = Math.max(0, reviews.length - effectiveSlidesPerView);
      if (currentSlide < maxSlide) {
        currentSlide++;
      } else if (loop) {
        currentSlide = 0;
      }
      updateSlider();
    };
    
    const prevSlide = () => {
      const isMobile = window.innerWidth <= 768;
      const effectiveSlidesPerView = isMobile ? 1 : slidesPerView;
      if (currentSlide > 0) {
        currentSlide--;
      } else if (loop) {
        currentSlide = Math.max(0, reviews.length - effectiveSlidesPerView);
      }
      updateSlider();
    };
    
    // Event listeners
    if (nextBtn) {
      nextBtn.addEventListener('click', nextSlide);
    }
    
    if (prevBtn) {
      prevBtn.addEventListener('click', prevSlide);
    }
    
    // Touch/swipe support
    let startX = 0;
    let startY = 0;
    let isDragging = false;
    
    track.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isDragging = true;
      this.stopAutoplay();
    });
    
    track.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = startX - currentX;
      const diffY = startY - currentY;
      
      // Only handle horizontal swipes
      if (Math.abs(diffX) > Math.abs(diffY)) {
        e.preventDefault();
      }
    });
    
    track.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      
      const endX = e.changedTouches[0].clientX;
      const diffX = startX - endX;
      
      if (Math.abs(diffX) > 50) { // Minimum swipe distance
        if (diffX > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
      }
      
      isDragging = false;
      this.startAutoplay();
    });
    
    // Autoplay
    const startAutoplay = () => {
      const isMobile = window.innerWidth <= 768;
      const effectiveSlidesPerView = isMobile ? 1 : slidesPerView;
      if (autoplay && reviews.length > effectiveSlidesPerView) {
        autoplayInterval = setInterval(nextSlide, autoplaySpeed);
      }
    };
    
    const stopAutoplay = () => {
      if (autoplayInterval) {
        clearInterval(autoplayInterval);
        autoplayInterval = null;
      }
    };
    
    // Pause autoplay on hover
    slider.addEventListener('mouseenter', stopAutoplay);
    slider.addEventListener('mouseleave', startAutoplay);
    
    // Store autoplay functions for external control
    this.startAutoplay = startAutoplay;
    this.stopAutoplay = stopAutoplay;
    
    // Initialize
    updateSlider();
    startAutoplay();
    
    // Update on resize
    window.addEventListener('resize', () => {
      updateSlider();
    });
  }

  /**
   * Get slides per view based on screen size
   */
  getSlidesPerView(desktop, tablet, mobile) {
    if (window.innerWidth <= 768) {
      return mobile;
    } else if (window.innerWidth <= 1024) {
      return tablet;
    } else {
      return desktop;
    }
  }

  /**
   * Create dots navigation
   */
  createDots(container, totalSlides, slidesPerView, goToSlideCallback) {
    container.innerHTML = '';
    const totalDots = Math.ceil(totalSlides / slidesPerView);
    
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.className = 'community-movement__slider-dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.addEventListener('click', () => {
        const isMobile = window.innerWidth <= 768;
        const effectiveSlidesPerView = isMobile ? 1 : slidesPerView;
        const slideIndex = i * effectiveSlidesPerView;
        if (goToSlideCallback) {
          goToSlideCallback(slideIndex);
        }
      });
      container.appendChild(dot);
    }
  }

  /**
   * Update slider position
   */
  updateSliderPosition(track, slideIndex, slideWidth) {
    const translateX = -(slideIndex * slideWidth);
    track.style.transform = `translateX(${translateX}%)`;
  }

  /**
   * Update dots active state
   */
  updateDots(container, currentSlide, slidesPerView) {
    if (!container) return;
    
    const dots = container.querySelectorAll('.community-movement__slider-dot');
    const activeDotIndex = Math.floor(currentSlide / slidesPerView);
    
    dots.forEach((dot, index) => {
      dot.classList.toggle('community-movement__slider-dot--active', index === activeDotIndex);
    });
  }

  /**
   * Update arrow states
   */
  updateArrows(prevBtn, nextBtn, currentSlide, maxSlide, loop) {
    if (prevBtn) {
      prevBtn.style.opacity = (currentSlide > 0 || loop) ? '1' : '0.5';
      prevBtn.disabled = !(currentSlide > 0 || loop);
    }
    
    if (nextBtn) {
      nextBtn.style.opacity = (currentSlide < maxSlide || loop) ? '1' : '0.5';
      nextBtn.disabled = !(currentSlide < maxSlide || loop);
    }
  }

  /**
   * Initialize counter animations
   */
  initCounters(section) {
    const counters = section.querySelectorAll('.community-movement__stat-number');
    
    if (counters.length === 0) return;
    
    // Create intersection observer for scroll-triggered animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.animatedStats.has(entry.target)) {
            this.animateCounter(entry.target);
            this.animatedStats.add(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px'
      }
    );

    counters.forEach(counter => {
      observer.observe(counter);
    });
  }

  /**
   * Animate a single counter
   */
  animateCounter(element) {
    const targetValue = parseFloat(element.dataset.target);
    const prefix = element.dataset.prefix || '';
    const suffix = element.dataset.suffix || '';
    const delay = parseInt(element.dataset.delay) || 0;
    const duration = parseInt(element.closest('.community-movement').dataset.countDuration) || 2000;
    const separator = element.closest('.community-movement').dataset.thousandSeparator || 'comma';
    
    const counter = element.querySelector('.community-counter');
    if (!counter || isNaN(targetValue)) return;

    // Check if user prefers reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      counter.textContent = prefix + this.formatNumber(Math.floor(targetValue), separator) + suffix;
      return;
    }
    
    setTimeout(() => {
      this.animateNumber(counter, targetValue, prefix, suffix, duration, separator);
    }, delay);
  }

  /**
   * Animate number counting
   */
  animateNumber(element, targetValue, prefix, suffix, duration, separator) {
    const startTime = performance.now();
    const startValue = 0;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (easeOutExpo)
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      
      const currentValue = startValue + (targetValue - startValue) * easeProgress;
      
      // Always use whole numbers during animation
      const currentWholeNumber = Math.floor(currentValue);
      
      // Format the number
      const formattedValue = this.formatNumber(currentWholeNumber, separator);
      element.textContent = prefix + formattedValue + suffix;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Ensure final value is exact
        element.textContent = prefix + this.formatNumber(targetValue, separator) + suffix;
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Format number with thousand separator
   */
  formatNumber(number, separator) {
    // Always use whole numbers (no decimals)
    const wholeNumber = Math.floor(number);
    
    // Apply thousand separator if enabled
    if (separator !== 'none' && wholeNumber.toString().length > 3) {
      return wholeNumber.toString().replace(/\B(?=(\d{3})+(?!\d))/g, this.getSeparatorChar(separator));
    }
    
    return wholeNumber.toString();
  }

  /**
   * Get separator character
   */
  getSeparatorChar(separator) {
    switch (separator) {
      case 'comma': return ',';
      case 'dot': return '.';
      case 'space': return ' ';
      default: return ',';
    }
  }

  /**
   * Reset all counters to 0
   */
  resetCounters(section) {
    const counters = section.querySelectorAll('.community-counter');
    counters.forEach(counter => {
      const prefix = counter.parentElement.dataset.prefix || '';
      const suffix = counter.parentElement.dataset.suffix || '';
      counter.textContent = prefix + '0' + suffix;
    });
    
    // Clear animated stats set
    this.animatedStats.clear();
  }

  /**
   * Animate all counters in section
   */
  animateCounters(section) {
    const counters = section.querySelectorAll('.community-movement__stat-number');
    counters.forEach(counter => {
      if (!this.animatedStats.has(counter)) {
        this.animateCounter(counter);
        this.animatedStats.add(counter);
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CommunityMovement();
  });
} else {
  new CommunityMovement();
}

// Reinitialize on Shopify section events (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new CommunityMovement();
  });
  
  document.addEventListener('shopify:section:unload', () => {
    // Clean up if needed
  });
}

// Export for external use
window.CommunityMovement = CommunityMovement;
